import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Calendar from './Calendar';
import StudentAttendanceView from './StudentAttendanceView';
import StudentTimetableView from './StudentTimetableView';
import CompactStudentHeader from './CompactStudentHeader';

import MobilePopup from './MobilePopup';
import ElegantMarksheet from './ElegantMarksheet';
import ChangePassword from './ChangePassword';
import StudentBehaviorDisplay from './StudentBehaviorDisplay';
import GamesManager from './Games/GamesManager';

import AcademicHistoryTimeline from './AcademicHistoryTimeline';
import { API_BASE_URL } from '../config/api';
import { formatDateWithWeekdayIST, parseFromDatabase, getCurrentDateIST } from '../utils/timezone';
import StudentHome from './StudentHome';
import MyPerformanceCard from './MyPerformanceCard';
import JoinLiveClassButton from './LiveStream/JoinLiveClassButton';
import './StudentDashboard.css';

// Add CSS for navigation scrollbar hiding
const navStyle = document.createElement('style');
navStyle.textContent = `
  .nav-scroll::-webkit-scrollbar {
    display: none;
  }
`;
if (!document.head.querySelector('style[data-nav-scroll]')) {
  navStyle.setAttribute('data-nav-scroll', 'true');
  document.head.appendChild(navStyle);
}

function StudentDashboard({ user, onLogout, isImpersonated = false, impersonationType = 'teacher' }) {
  const [attendance, setAttendance] = useState([]);
  const [content, setContent] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [classTeacher, setClassTeacher] = useState(null);
  const [onlineContent, setOnlineContent] = useState([]);
  const [courseLinks, setCourseLinks] = useState([]);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [libraryContent, setLibraryContent] = useState([]);
  const [marksheet, setMarksheet] = useState(null);
  const [showMarksheet, setShowMarksheet] = useState(false);
  const [activeAcademicYear, setActiveAcademicYear] = useState('');
  const [idCard, setIdCard] = useState(null);
  const [showIdCard, setShowIdCard] = useState(false);
  const [classworkData, setClassworkData] = useState([]);
  const [showFullProfile, setShowFullProfile] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [behaviorEvaluation, setBehaviorEvaluation] = useState(null);
  const [activeSection, setActiveSection] = useState('timetable');
  const [showHome, setShowHome] = useState(true);


  // Handle logout with device session cleanup
  const handleLogout = async () => {
    try {
      // Only cleanup session for students
      if (user.role === 'student') {
        const deviceId = localStorage.getItem('deviceId');
        const userId = user.id;
        
        if (deviceId && userId) {
          await axios.post(`${API_BASE_URL}/api/logout`, { userId, deviceId });
        }
      }
      
      // Call the parent logout function
      onLogout();
    } catch (error) {
      console.error('Logout error:', error);
      // Still logout even if session cleanup fails
      onLogout();
    }
  };

  useEffect(() => {
    fetchAttendance();
    fetchContent();
    fetchAnnouncements();
    fetchUserProfile();
    fetchClassTeacher();
    fetchOnlineContent();
    fetchCourseLinks();
    fetchLibraryContent();
    fetchIdCard();
    fetchClasswork();
    fetchBehaviorEvaluation();
    fetchActiveAcademicYear();
    
    // Start session validation for students
    if (user.role === 'student') {
      startSessionValidation();
      
      // Initial session validation disabled to prevent unwanted logouts
      console.log('Initial session validation skipped - no automatic logout on page load');
    }
  }, []);

  // Session validation for students (less aggressive)
  const startSessionValidation = () => {
    const deviceId = localStorage.getItem('deviceId');
    const userId = user.id;
    
    if (!deviceId || !userId) {
      console.log('No device session found, skipping validation');
      return;
    }

    // Disabled automatic session validation to prevent unwanted logouts
    console.log('Session validation disabled - students will not be automatically logged out');

    // No cleanup needed since session validation is disabled
    return () => {};
  };

  // Body scroll lock for mobile popups (Calendar handles its own scroll lock)
  useEffect(() => {
    if (window.innerWidth <= 768 && (showMarksheet || showIdCard || showLibraryModal)) {
      window.scrollTo(0, 0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMarksheet, showIdCard, showLibraryModal]);

  const fetchAttendance = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/attendance/${user.id}`);
      setAttendance(response.data);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/content/${user.class}`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/announcements/${user.class}`);
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      const currentUser = response.data.find(u => u._id === user.id);
      setUserProfile(currentUser);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchClassTeacher = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      const teacher = response.data.find(u => u.role === 'teacher' && u.class === user.class);
      setClassTeacher(teacher);
    } catch (error) {
      console.error('Error fetching class teacher:', error);
    }
  };

  const fetchOnlineContent = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/online-content/${user.class}`);
      setOnlineContent(response.data);
    } catch (error) {
      console.error('Error fetching online content:', error);
    }
  };

  const fetchCourseLinks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/course-links/${user.class}`);
      setCourseLinks(response.data);
    } catch (error) {
      console.error('Error fetching course links:', error);
    }
  };

  const fetchLibraryContent = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/course-links/${user.class}`);
      setLibraryContent(response.data);
    } catch (error) {
      console.error('Error fetching library content:', error);
    }
  };

  const fetchMarksheet = async (yearOverride) => {
    try {
      const year = yearOverride || activeAcademicYear;
      if (!year) return;
      const response = await axios.get(`${API_BASE_URL}/api/marksheet/${user.id}/year/${encodeURIComponent(year)}`);
      setMarksheet(response.data);
    } catch {
      setMarksheet(null);
    }
  };

  const fetchActiveAcademicYear = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/academic-year/current`);
      const year = res.data?.year || '2026-2027';
      setActiveAcademicYear(year);
      fetchMarksheet(year);
    } catch {
      setActiveAcademicYear('2026-2027');
      fetchMarksheet('2026-2027');
    }
  };

  const fetchIdCard = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/idcard/${user.id}`);
      if (response.data) {
        // Ensure class is set correctly
        setIdCard({
          ...response.data,
          class: response.data.class || user.class
        });
      }
    } catch (error) {
      console.error('Error fetching ID card:', error);
    }
  };

  const fetchClasswork = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/classwork/${user.class}`);
      setClassworkData(response.data);
    } catch (error) {
      console.error('Error fetching classwork:', error);
    }
  };

  const fetchBehaviorEvaluation = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/behavior-evaluation/${user.id}`);
      setBehaviorEvaluation(response.data?.behaviorRatings || null);
    } catch (error) {
      console.error('Error fetching behavior evaluation:', error);
      setBehaviorEvaluation(null);
    }
  };

  const downloadIdCard = () => {
    if (!idCard) {
      alert('No ID card data available');
      return;
    }
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Simple dimensions matching display (350x220 scaled up)
    canvas.width = 1050; // 350 * 3
    canvas.height = 660;  // 220 * 3
    const scale = 3;
    
    // School settings
    const schoolSettings = {
      name: localStorage.getItem('schoolName') || 'The Rising School',
      address: localStorage.getItem('schoolAddress') || 'Savina, Udaipur',
      phone: localStorage.getItem('schoolPhone') || '8209683549'
    };
    
    // Main blue background
    ctx.fillStyle = '#1e40af';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Rounded corners
    ctx.globalCompositeOperation = 'destination-in';
    ctx.beginPath();
    ctx.roundRect(0, 0, canvas.width, canvas.height, 10 * scale);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
    
    // White border
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.roundRect(3, 3, canvas.width-6, canvas.height-6, 7 * scale);
    ctx.stroke();
    
    // Header section
    const headerHeight = 64 * scale;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(0, 0, canvas.width, headerHeight, [8 * scale, 8 * scale, 0, 0]);
    ctx.fill();
    
    // Logo area
    const logoSize = 50 * scale;
    const logoX = 15 * scale;
    const logoY = 12 * scale;
    ctx.fillStyle = '#1e40af';
    ctx.beginPath();
    ctx.roundRect(logoX, logoY, logoSize, logoSize, 8 * scale);
    ctx.fill();
    
    // Try to load logo
    const logoImg = new Image();
    logoImg.crossOrigin = 'anonymous';
    logoImg.onload = () => {
      ctx.drawImage(logoImg, logoX + 5 * scale, logoY + 5 * scale, logoSize - 10 * scale, logoSize - 10 * scale);
      drawContent();
    };
    logoImg.onerror = () => {
      ctx.fillStyle = 'white';
      ctx.font = `${20 * scale}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText('🏫', logoX + logoSize/2, logoY + logoSize/2 + 7 * scale);
      drawContent();
    };
    
    // School info
    ctx.fillStyle = '#1e40af';
    ctx.font = `bold ${20 * scale}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText(schoolSettings.name, logoX + logoSize + 10 * scale, logoY + 16 * scale);
    
    ctx.fillStyle = '#64748b';
    ctx.font = `${12 * scale}px Arial`;
    ctx.fillText(schoolSettings.address, logoX + logoSize + 10 * scale, logoY + 28 * scale);
    ctx.fillText(`📞 ${schoolSettings.phone}`, logoX + logoSize + 10 * scale, logoY + 38 * scale);
    
    // Year badge
    const badgeWidth = 60 * scale;
    const badgeHeight = 24 * scale;
    const badgeX = canvas.width - badgeWidth - 15 * scale;
    const badgeY = 20 * scale;
    ctx.fillStyle = '#dc2626';
    ctx.beginPath();
    ctx.roundRect(badgeX, badgeY, badgeWidth, badgeHeight, 4 * scale);
    ctx.fill();
    
    ctx.fillStyle = 'white';
    ctx.font = `bold ${14 * scale}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(activeAcademicYear || '2026-2027', badgeX + badgeWidth/2, badgeY + 16 * scale);
    
    const drawContent = () => {
      // Photo area
      const photoX = 15 * scale;
      const photoY = headerHeight + 15 * scale;
      const photoWidth = 70 * scale;
      const photoHeight = 85 * scale;
      
      ctx.fillStyle = '#f3f4f6';
      ctx.beginPath();
      ctx.roundRect(photoX, photoY, photoWidth, photoHeight, 6 * scale);
      ctx.fill();
      
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.roundRect(photoX, photoY, photoWidth, photoHeight, 6 * scale);
      ctx.stroke();
      
      const drawPhoto = () => {
        if (userProfile?.profilePicture) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            ctx.save();
            ctx.beginPath();
            ctx.roundRect(photoX + 3, photoY + 3, photoWidth - 6, photoHeight - 6, 6 * scale);
            ctx.clip();
            
            const imgAspect = img.width / img.height;
            const frameAspect = (photoWidth - 6) / (photoHeight - 6);
            
            let drawWidth, drawHeight, drawX, drawY;
            
            if (imgAspect > frameAspect) {
              drawHeight = photoHeight - 6;
              drawWidth = drawHeight * imgAspect;
              drawX = photoX + 3 - (drawWidth - (photoWidth - 6)) / 2;
              drawY = photoY + 3;
            } else {
              drawWidth = photoWidth - 6;
              drawHeight = drawWidth / imgAspect;
              drawX = photoX + 3;
              drawY = photoY + 3 - (drawHeight - (photoHeight - 6)) / 2;
            }
            
            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            ctx.restore();
            
            // Download after photo is drawn
            setTimeout(() => {
              const link = document.createElement('a');
              link.download = `${userProfile?.name || idCard.name || user.name}_School_ID_Card.png`;
              link.href = canvas.toDataURL('image/png', 1.0);
              link.click();
            }, 100);
          };
          img.onerror = () => {
            ctx.fillStyle = '#6b7280';
            ctx.font = `${24 * scale}px Arial`;
            ctx.textAlign = 'center';
            ctx.fillText('👤', photoX + photoWidth/2, photoY + photoHeight/2 + 8 * scale);
            
            // Download with fallback icon
            setTimeout(() => {
              const link = document.createElement('a');
              link.download = `${userProfile?.name || idCard.name || user.name}_School_ID_Card.png`;
              link.href = canvas.toDataURL('image/png', 1.0);
              link.click();
            }, 100);
          };
          img.src = `${API_BASE_URL}${userProfile.profilePicture}`;
        } else {
          ctx.fillStyle = '#6b7280';
          ctx.font = `${24 * scale}px Arial`;
          ctx.textAlign = 'center';
          ctx.fillText('👤', photoX + photoWidth/2, photoY + photoHeight/2 + 8 * scale);
          
          // Download with fallback icon
          setTimeout(() => {
            const link = document.createElement('a');
            link.download = `${userProfile?.name || idCard.name || user.name}_School_ID_Card.png`;
            link.href = canvas.toDataURL('image/png', 1.0);
            link.click();
          }, 100);
        }
      };
      
      drawPhoto();
      
      // ID badge
      const idBadgeY = photoY + photoHeight + 8 * scale;
      const idBadgeWidth = 70 * scale;
      const idBadgeHeight = 18 * scale;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.roundRect(photoX, idBadgeY, idBadgeWidth, idBadgeHeight, 4 * scale);
      ctx.fill();
      
      ctx.fillStyle = '#1e40af';
      ctx.font = `bold ${12 * scale}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(`SR.No : ${idCard.scholarNo || user.id?.slice(-6)}`, photoX + idBadgeWidth/2, idBadgeY + 12 * scale);
      
      // Student info
      const infoX = photoX + photoWidth + 15 * scale;
      const infoY = photoY;
      
      ctx.fillStyle = 'white';
      ctx.textAlign = 'left';
      ctx.font = `bold ${20 * scale}px Arial`;
      ctx.fillText(userProfile?.name || idCard.name || user.name, infoX, infoY + 20 * scale);
      
      ctx.font = `${14 * scale}px Arial`;
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.fillText(`Class: ${user.class}`, infoX, infoY + 35 * scale);
      
      // Details
      ctx.font = `${14 * scale}px Arial`;
      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      let detailY = infoY + 55 * scale;
      const lineHeight = 16 * scale;
      
      ctx.fillText(`DOB: ${idCard.dob ? new Date(idCard.dob).toLocaleDateString('en-GB') : 'N/A'}`, infoX, detailY);
      detailY += lineHeight;
      
      const fatherName = (idCard.fathersName || 'N/A');
      const displayFatherName = fatherName.length > 15 ? fatherName.substring(0, 15) + '...' : fatherName;
      ctx.fillText(`Father: ${displayFatherName}`, infoX, detailY);
      detailY += lineHeight;
      
      ctx.fillText(`Mobile: ${idCard.mobile || 'N/A'}`, infoX, detailY);
      detailY += lineHeight;
      
      const address = (idCard.address || 'N/A');
      const displayAddress = address.length > 20 ? address.substring(0, 20) + '...' : address;
      ctx.fillText(`Address: ${displayAddress}`, infoX, detailY);
      
      // Validity
      const validityX = canvas.width - 90 * scale;
      const validityY = canvas.height - 40 * scale;
      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      ctx.beginPath();
      ctx.roundRect(validityX, validityY, 75 * scale, 30 * scale, 4 * scale);
      ctx.fill();
      
      ctx.fillStyle = 'white';
      ctx.font = `${9 * scale}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText('Valid Until', validityX + 37.5 * scale, validityY + 12 * scale);
      ctx.fillText('March ' + (activeAcademicYear || '2026-2027').split('-')[1], validityX + 37.5 * scale, validityY + 22 * scale);
      
    };
    
    logoImg.src = '/logo.png';
  };
  
  // Admin settings functions
  const updateSchoolSettings = () => {
    if (user.name === 'admin1' || user.email === 'a1@s.com' || user.role === 'admin1') {
      const newName = prompt('School Name:', localStorage.getItem('schoolName') || 'The Rising School');
      const newAddress = prompt('School Address:', localStorage.getItem('schoolAddress') || 'Savina, Udaipur');
      const newPhone = prompt('School Phone:', localStorage.getItem('schoolPhone') || '8209683549');
      
      if (newName !== null) localStorage.setItem('schoolName', newName || 'The Rising School');
      if (newAddress !== null) localStorage.setItem('schoolAddress', newAddress || 'Savina, Udaipur');
      if (newPhone !== null) localStorage.setItem('schoolPhone', newPhone || '8209683549');
      
      alert('School settings updated! Changes will reflect in all ID cards.');
      window.location.reload();
    } else {
      alert('Only admin users can modify school settings for all ID cards.');
    }
  };



  const viewReport = (type) => {
    alert(`${type} opened successfully!`);
  };

  const downloadMaterial = (item) => {
    if (item.fileUrl) {
      // Download actual file from uploads folder
      const link = document.createElement('a');
      link.href = `${API_BASE_URL}${item.fileUrl}`;
      link.download = item.title;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      alert(`Downloaded: ${item.title}`);
    } else {
      // Create sample file if no actual file exists
      const fileContent = `${item.title}\n\nDescription: ${item.description}\n\nType: ${item.type}\nUploaded by: ${item.uploadedBy?.name}\nDate: ${new Date(item.createdAt).toLocaleDateString()}`;
      
      const blob = new Blob([fileContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${item.title.replace(/[^a-z0-9]/gi, '_')}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert(`Downloaded: ${item.title}`);
    }
  };

  const downloadFile = (fileName) => {
    alert(`Downloading file: ${fileName}`);
  };

  const showSection = (sectionId) => {
    setActiveSection(sectionId);
  };

  const openFromHome = (sectionId) => {
    setActiveSection(sectionId);
    setShowHome(false);
    window.scrollTo(0, 0);
  };

  return (
    <div className="student-home-host">
      <JoinLiveClassButton />
      <StudentHome
        user={user}
        userProfile={userProfile}
        idCard={idCard}
        attendance={attendance}
        content={content}
        announcements={announcements}
        classworkData={classworkData}
        marksheet={marksheet}
        activeAcademicYear={activeAcademicYear}
        isImpersonated={isImpersonated}
        legacyOpen={!showHome}
        legacyTitle={activeSection}
        onCloseLegacy={() => setShowHome(true)}
        onViewMarksheet={() => setShowMarksheet(true)}
        onOpenSection={openFromHome}
        onChangePassword={() => setShowChangePassword(true)}
        onLogout={handleLogout}
      >
      <div className="dashboard-grid" style={{ 
        paddingTop: activeSection !== 'timetable' && activeSection !== 'attendance' ? '20px' : '0'
      }}>
        {activeSection === 'courses' && (
        <div id="courses" className="online-course-card" style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', color: 'white', gridColumn: 'span 2', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 12px 35px rgba(30, 64, 175, 0.4)', border: '2px solid rgba(255,255,255,0.1)' }}>
          <div style={{ padding: '24px 24px 16px 24px', background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)', backdropFilter: 'blur(15px)', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
            <h3 style={{ margin: '0', fontSize: '24px', fontWeight: '700', textAlign: 'center', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>🎓 Online Course</h3>
          </div>
          <div className="course-content" style={{ maxHeight: '400px', overflowY: 'auto', padding: '0 24px 24px 24px' }}>
            {[...onlineContent, ...courseLinks].length > 0 ? (
              <div>
                {['Hindi', 'English', 'Math', 'Science', 'EVS', 'Social_Studies', 'Urdu', 'Sanskrit'].map(subject => {
                  const subjectContent = onlineContent.filter(item => item.subject === subject);
                  const subjectLinks = courseLinks.filter(item => item.subject === subject);
                  const allContent = [...subjectContent, ...subjectLinks];
                  if (allContent.length === 0) return null;
                  
                  return (
                    <div key={subject} style={{ marginBottom: '24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '10px 16px', background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.9) 0%, rgba(217, 119, 6, 0.9) 100%)', borderRadius: '12px', backdropFilter: 'blur(10px)', boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)' }}>
                        <span style={{ fontSize: '20px' }}>📚</span>
                        <h4 style={{ color: '#fff', fontSize: '18px', margin: '0', fontWeight: '600' }}>{subject}</h4>
                      </div>
                      {subjectContent.map(item => (
                        <div key={item._id} className="course-item" style={{ marginBottom: '12px', padding: '16px', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)', borderRadius: '12px', backdropFilter: 'blur(10px)', border: '1px solid rgba(16, 185, 129, 0.3)', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)' }}>
                          <div className="course-item-content">
                            <div className="course-info">
                              <h5 style={{ fontSize: '16px', margin: '0 0 6px 0', fontWeight: '600' }}>{item.title}</h5>
                              <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: '0.9', lineHeight: '1.4' }}>{item.description}</p>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', opacity: '0.8' }}>
                                <span>👨‍🏫 {item.uploadedBy}</span>
                                <span>•</span>
                                <span>📅 {new Date(item.createdAt).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <button 
                              className="course-btn"
                              style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', padding: '10px 16px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', transition: 'all 0.3s ease', minWidth: '80px', boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
                              onClick={() => window.location.href = item.resourceLink}
                            >
                              🔗 Open
                            </button>
                          </div>
                        </div>
                      ))}
                      {courseLinks.map(item => (
                        <div key={item._id} className="course-item" style={{ marginBottom: '12px', padding: '16px', background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.1) 100%)', borderRadius: '12px', backdropFilter: 'blur(10px)', border: '1px solid rgba(139, 92, 246, 0.3)', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.1)' }}>
                          <div className="course-item-content">
                            <div className="course-info">
                              <h5 style={{ fontSize: '16px', margin: '0 0 6px 0', fontWeight: '600' }}>{item.title}</h5>
                              <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: '0.9', lineHeight: '1.4' }}>{item.description}</p>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', opacity: '0.8' }}>
                                <span>📹 {item.linkType}</span>
                                <span>•</span>
                                <span>👨‍🏫 {item.uploadedBy}</span>
                                <span>•</span>
                                <span>📅 {new Date(item.createdAt).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <button 
                              className="course-btn"
                              style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', padding: '10px 16px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', transition: 'all 0.3s ease', minWidth: '80px', boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
                              onClick={() => window.location.href = item.resourceLink}
                            >
                              🔗 Access
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
                {/* Complete Course Links Section */}
                {courseLinks.filter(item => !item.subject || item.subject === 'Complete Course').length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '10px 16px', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(5, 150, 105, 0.9) 100%)', borderRadius: '12px', backdropFilter: 'blur(10px)', boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)' }}>
                      <span style={{ fontSize: '20px' }}>🎓</span>
                      <h4 style={{ color: '#fff', fontSize: '18px', margin: '0', fontWeight: '600' }}>Complete Course</h4>
                    </div>
                    {courseLinks.filter(item => !item.subject || item.subject === 'Complete Course').map(item => (
                      <div key={item._id} className="course-item" style={{ marginBottom: '12px', padding: '16px', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)', borderRadius: '12px', backdropFilter: 'blur(10px)', border: '1px solid rgba(16, 185, 129, 0.3)', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)' }}>
                        <div className="course-item-content">
                          <div className="course-info">
                            <h5 style={{ fontSize: '16px', margin: '0 0 6px 0', fontWeight: '600' }}>{item.title}</h5>
                            <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: '0.9', lineHeight: '1.4' }}>{item.description}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', opacity: '0.8' }}>
                              <span>🎓 Complete Course</span>
                              <span>•</span>
                              <span>👨🏫 {item.uploadedBy}</span>
                              <span>•</span>
                              <span>📅 {new Date(item.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <button 
                            className="course-btn"
                            style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', padding: '10px 16px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', transition: 'all 0.3s ease', minWidth: '80px', boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
                            onClick={() => window.location.href = item.resourceLink}
                          >
                            🔗 Access
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', opacity: '0.8' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📚</div>
                <p style={{ fontSize: '18px', marginBottom: '8px', fontWeight: '600' }}>No course content available yet</p>
                <p style={{ fontSize: '14px', opacity: '0.8' }}>Your teachers will add learning materials here</p>
              </div>
            )}
          </div>
        </div>
        )}

        {activeSection === 'performance' && <MyPerformanceCard user={user} />}

        {activeSection === 'history' && (
        <div id="history" className="professional-card" style={{ gridColumn: 'span 2', background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '2px solid #3b82f6', boxShadow: '0 8px 25px rgba(59,130,246,0.15)' }}>
          <AcademicHistoryTimeline studentId={user.id} />
        </div>
        )}

        {activeSection === 'timetable' && (
        <div id="timetable" className="professional-card" style={{ gridColumn: 'span 2', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', border: '2px solid #0ea5e9', boxShadow: '0 8px 25px rgba(14, 165, 233, 0.15)' }}>
          <h3 className="card-title" style={{ color: '#0c4a6e', borderBottom: '2px solid #0ea5e9', paddingBottom: '8px' }}>📅 My Timetable</h3>
          <StudentTimetableView userClass={user.class} userId={user.id} autoLoad={true} />
        </div>
        )}

        {activeSection === 'attendance' && (
        <div id="attendance">
          <StudentAttendanceView userClass={user.class} userId={user.id} />
        </div>
        )}

        {activeSection === 'teacher' && (
        <div id="teacher" className="professional-card" style={{ background: 'linear-gradient(135deg, #fefefe 0%, #f0f9ff 100%)', border: '2px solid #dbeafe', boxShadow: '0 8px 25px rgba(59, 130, 246, 0.1)' }}>
          <h3 className="card-title" style={{ color: '#1e40af', borderBottom: '2px solid #dbeafe', paddingBottom: '8px' }}>🧑🏫 Class Teacher</h3>
          <div style={{ textAlign: 'center' }}>
            <div 
              style={{ 
                width: '150px', 
                height: '150px', 
                borderRadius: '10px', 
                background: classTeacher?.profilePicture 
                  ? `url(${classTeacher.profilePicture}) center/cover no-repeat` 
                  : 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '60px', 
                margin: '0 auto 16px', 
                border: '3px solid #f8f9fa', 
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}
              className="responsive-avatar"
            >
              {!classTeacher?.profilePicture && '👨🏫'}
            </div>
            <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', margin: '0 0 8px 0' }}>
              {classTeacher?.name || 'No Teacher Assigned'}
            </h4>
            <p style={{ color: '#6c757d', margin: '0 0 8px 0', fontSize: '14px' }}>Class: {user.class} Teacher</p>
            <div style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', padding: '10px 16px', borderRadius: '10px', display: 'inline-block', border: '1px solid #f59e0b' }}>
              <span style={{ color: '#92400e', fontSize: '13px', fontWeight: '600' }}>📧 {classTeacher?.email || 'N/A'}</span>
            </div>
          </div>
        </div>
        )}

        {activeSection === 'behavior' && (
        <div id="behavior" className="professional-card" style={{ gridColumn: 'span 2' }}>
          <StudentBehaviorDisplay 
            behaviorData={behaviorEvaluation}
            studentName={user.name}
            viewMode="student"
            colorful={true}
          />
        </div>
        )}

        {activeSection === 'materials' && (
        <div id="materials" className="professional-card" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '2px solid #f59e0b', boxShadow: '0 8px 25px rgba(245, 158, 11, 0.15)' }}>
          <h3 className="card-title" style={{ color: '#92400e', borderBottom: '2px solid #f59e0b', paddingBottom: '8px' }}>📚 Study Materials</h3>
          {content.length > 0 ? content.map(item => (
            <div key={item._id} style={{ marginBottom: '10px', padding: '12px', background: 'rgba(255,255,255,0.8)', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong style={{ color: '#333', fontSize: '15px' }}>{item.title}</strong>
                  <p style={{ color: '#666', margin: '4px 0', fontSize: '14px' }}>{item.description}</p>
                  <small style={{ color: '#888' }}>Type: {item.type} | By: {item.uploadedBy?.name}</small>
                  {item.fileUrl && (
                    <div style={{ marginTop: '5px' }}>
                      <small style={{ color: '#007bff', background: 'rgba(0,123,255,0.1)', padding: '2px 6px', borderRadius: '4px' }}>📎 File attached</small>
                    </div>
                  )}
                </div>
                <div>
                  {item.fileUrl ? (
                    <div>
                      <button style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', marginBottom: '5px', boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)', fontWeight: '500' }} onClick={() => downloadMaterial(item)}>📥 Download</button>
                      <br/>
                      <button style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '10px', boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)', fontWeight: '500' }} onClick={() => window.location.href = `${API_BASE_URL}${item.fileUrl}`}>👁️ View</button>
                    </div>
                  ) : (
                    <button style={{ background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)', fontWeight: '500' }} onClick={() => downloadMaterial(item)}>📥 Download Info</button>
                  )}
                </div>
              </div>
            </div>
          )) : <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>No study materials available yet</p>}
        </div>
        )}

        {activeSection === 'profile' && (
        <div id="profile" className="professional-card" style={{ gridColumn: 'span 2', background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', border: '2px solid #8b5cf6', boxShadow: '0 8px 25px rgba(139, 92, 246, 0.15)' }}>
          <h3 className="card-title" style={{ color: '#5b21b6', borderBottom: '2px solid #8b5cf6', paddingBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            📊 My Complete Profile
            <button 
              onClick={() => setShowFullProfile(!showFullProfile)}
              style={{ 
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
                color: 'white', 
                border: 'none', 
                padding: '8px 16px', 
                borderRadius: '8px', 
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600'
              }}
            >
              {showFullProfile ? '🔼 Hide' : '🔽 Full View'}
            </button>
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginTop: '15px', maxHeight: showFullProfile ? 'none' : '200px', overflow: 'hidden' }}>
            <div style={{ background: 'rgba(255,255,255,0.7)', padding: '15px', borderRadius: '10px' }}>
              <h4 style={{ color: '#5b21b6', marginBottom: '10px' }}>📚 Academic Performance</h4>
              <p><strong>Overall Grade:</strong> {userProfile?.overallGrade || 'B+ (85%)'}</p>
              <p><strong>Math:</strong> {userProfile?.mathGrade || '88% (A-)'}</p>
              <p><strong>Science:</strong> {userProfile?.scienceGrade || '82% (B+)'}</p>
              <p><strong>English:</strong> {userProfile?.englishGrade || '85% (B+)'}</p>
            </div>
            
            <div style={{ background: 'rgba(255,255,255,0.7)', padding: '15px', borderRadius: '10px' }}>
              <h4 style={{ color: '#5b21b6', marginBottom: '10px' }}>📅 Attendance Record</h4>
              <p><strong>Total Days:</strong> {userProfile?.totalDays || '180'}</p>
              <p><strong>Present:</strong> {userProfile?.presentDays || '165 days (92%)'}</p>
              <p><strong>Absent:</strong> {userProfile?.absentDays || '12'} days</p>
              <p><strong>Late:</strong> {userProfile?.lateDays || '3'} days</p>
            </div>
            
            <div style={{ background: 'rgba(255,255,255,0.7)', padding: '15px', borderRadius: '10px' }}>
              <h4 style={{ color: '#5b21b6', marginBottom: '10px' }}>⚖️ Discipline Record</h4>
              <p><strong>Behavior Score:</strong> {userProfile?.behaviorScore || '95/100'}</p>
              <p><strong>Warnings:</strong> {userProfile?.warnings || '1'}</p>
              <p><strong>Violations:</strong> {userProfile?.violations || '0'}</p>
              <p><strong>Commendations:</strong> {userProfile?.commendations || '3'}</p>
            </div>
            
            <div style={{ background: 'rgba(255,255,255,0.7)', padding: '15px', borderRadius: '10px' }}>
              <h4 style={{ color: '#5b21b6', marginBottom: '10px' }}>📝 Assignment Status</h4>
              <p><strong>Completed:</strong> {userProfile?.completedAssignments || '28/30 (93%)'}</p>
              <p><strong>On Time:</strong> {userProfile?.onTimeSubmissions || '26/28 (93%)'}</p>
              <p><strong>Late Submissions:</strong> {userProfile?.lateSubmissions || '2'}</p>
              <p><strong>Missing:</strong> {userProfile?.missingAssignments || '2'}</p>
            </div>
            
            <div style={{ background: 'rgba(255,255,255,0.7)', padding: '15px', borderRadius: '10px' }}>
              <h4 style={{ color: '#5b21b6', marginBottom: '10px' }}>👨👩👧👦 Parent Contact</h4>
              <p><strong>Guardian:</strong> {userProfile?.guardianName || 'Mr. & Mrs. Parent'}</p>
              <p><strong>Phone:</strong> {userProfile?.guardianPhone || '+1-234-567-8900'}</p>
              <p><strong>Email:</strong> {userProfile?.guardianEmail || 'parent@email.com'}</p>
              <p><strong>Last Contact:</strong> {userProfile?.lastContact || 'March 10, 2024'}</p>
            </div>
            
            <div style={{ background: 'rgba(255,255,255,0.7)', padding: '15px', borderRadius: '10px' }}>
              <h4 style={{ color: '#5b21b6', marginBottom: '10px' }}>ℹ️ Personal Information</h4>
              <p><strong>Name:</strong> {userProfile?.name || user.name}</p>
              <p><strong>Class:</strong> {userProfile?.class || user.class}</p>
              <p><strong>Father's Name:</strong> {userProfile?.fathersName || 'N/A'}</p>
              <p><strong>Student ID:</strong> {userProfile?.studentId || user.id?.slice(-6)}</p>
              <div style={{ marginTop: '15px', textAlign: 'center' }}>
                <button 
                  onClick={() => setShowChangePassword(true)}
                  style={{ 
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
                    color: 'white', 
                    border: 'none', 
                    padding: '10px 20px', 
                    borderRadius: '10px', 
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  🔐 Change Password
                </button>
              </div>
            </div>
          </div>
          
          {!showFullProfile && (
            <div style={{ 
              textAlign: 'center', 
              marginTop: '15px', 
              padding: '15px',
              background: 'rgba(139, 92, 246, 0.1)',
              borderRadius: '10px',
              border: '1px solid rgba(139, 92, 246, 0.3)'
            }}>
              <p style={{ margin: '0 0 10px 0', color: '#5b21b6', fontSize: '14px', fontWeight: '600' }}>🔐 Account Security</p>
              <button 
                onClick={() => setShowChangePassword(true)}
                style={{ 
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
                  color: 'white', 
                  border: 'none', 
                  padding: '10px 20px', 
                  borderRadius: '10px', 
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              >
                🔐 Change My Password
              </button>
            </div>
          )}

        </div>
        )}

        {activeSection === 'marksheet' && (
        <div className="professional-card" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', border: '2px solid #0ea5e9', boxShadow: '0 8px 25px rgba(14, 165, 233, 0.15)' }}>
          <h3 className="card-title" style={{ color: '#0c4a6e', borderBottom: '2px solid #0ea5e9', paddingBottom: '8px' }}>🔔 Announcements</h3>
          <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '10px' }}>
            {announcements.length > 0 ? announcements.map(announcement => (
              <div key={announcement._id} style={{ padding: '8px', background: announcement.type === 'school' ? '#e3f2fd' : '#f3e5f5', borderRadius: '4px', marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>{announcement.title || (announcement.type === 'school' ? 'School Announcement' : 'Class Announcement')}</strong>
                  <small>{parseFromDatabase(announcement.createdAt, { month: 'short', day: 'numeric' })}</small>
                </div>
                <p>{announcement.message}</p>
                <small>By: {announcement.createdBy} | {announcement.type === 'school' ? 'School-wide' : `Class ${announcement.class}`}</small>
              </div>
            )) : (
              <div style={{ padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
                <p>No announcements yet</p>
              </div>
            )}
          </div>
        </div>

        )}



        {activeSection === 'idcard' && (
        <div className="professional-card" style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', border: '2px solid #8b5cf6', boxShadow: '0 8px 25px rgba(139, 92, 246, 0.15)' }}>
          <h3 className="card-title" style={{ color: '#5b21b6', borderBottom: '2px solid #8b5cf6', paddingBottom: '8px' }}>💻 Online Content</h3>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {onlineContent.length > 0 ? (
              <div>
                {['Hindi', 'English', 'Math', 'Science', 'EVS', 'Social_Studies', 'Urdu', 'Sanskrit'].map(subject => {
                  const subjectContent = onlineContent.filter(item => item.subject === subject);
                  if (subjectContent.length === 0) return null;
                  
                  return (
                    <div key={subject} style={{ marginBottom: '15px' }}>
                      <h4 style={{ color: '#007bff', fontSize: '16px', marginBottom: '8px' }}>📚 {subject}</h4>
                      {subjectContent.map(item => (
                        <div key={item._id} style={{ marginBottom: '8px', padding: '8px', background: '#f8f9fa', borderRadius: '4px', borderLeft: '3px solid #007bff' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <strong>{item.title}</strong>
                              <p style={{ margin: '2px 0', fontSize: '14px', color: '#666' }}>{item.description}</p>
                              <small>By: {item.uploadedBy} | {new Date(item.createdAt).toLocaleDateString()}</small>
                            </div>
                            <div>
                              <button 
                                className="btn" 
                                style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)' }}
                                onClick={() => window.location.href = item.resourceLink}
                              >
                                🔗 Open Link
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p>No online content available yet</p>
            )}
          </div>
        </div>
        )}

        {activeSection === 'marksheet' && (
        <>
        <div className="professional-card" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', border: '2px solid #0ea5e9', boxShadow: '0 8px 25px rgba(14, 165, 233, 0.15)' }}>
          <h3 className="card-title" style={{ color: '#0c4a6e', borderBottom: '2px solid #0ea5e9', paddingBottom: '8px' }}>🔔 Announcements</h3>
          <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '10px' }}>
            {announcements.length > 0 ? announcements.map(announcement => (
              <div key={announcement._id} style={{ padding: '8px', background: announcement.type === 'school' ? '#e3f2fd' : '#f3e5f5', borderRadius: '4px', marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>{announcement.title || (announcement.type === 'school' ? 'School Announcement' : 'Class Announcement')}</strong>
                  <small>{parseFromDatabase(announcement.createdAt, { month: 'short', day: 'numeric' })}</small>
                </div>
                <p>{announcement.message}</p>
                <small>By: {announcement.createdBy} | {announcement.type === 'school' ? 'School-wide' : `Class ${announcement.class}`}</small>
              </div>
            )) : (
              <div style={{ padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
                <p>No announcements yet</p>
              </div>
            )}
          </div>
        </div>

        <div className="professional-card" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '2px solid #f59e0b', boxShadow: '0 8px 25px rgba(245, 158, 11, 0.15)' }}>
          <h3 className="card-title" style={{ color: '#92400e', borderBottom: '2px solid #f59e0b', paddingBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            📊 Current Year Results
            <span style={{ fontSize: 12, fontWeight: 600, color: '#1e40af', background: '#dbeafe', padding: '3px 10px', borderRadius: 20 }}>
              {activeAcademicYear}
            </span>
          </h3>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            {marksheet ? (
              <div>
                <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>Class {marksheet.class} — {marksheet.academicYear}</p>
                <p style={{ color: '#666', marginBottom: '15px' }}>Click "View Results" to see your complete marksheet</p>
                <div style={{ display: 'flex', justifyContent: 'space-around', fontSize: '14px', marginBottom: 16 }}>
                  <div><strong>Academic Year:</strong><br/>{marksheet.academicYear}</div>
                  <div><strong>Class Teacher:</strong><br/>{marksheet.classTeacher || 'N/A'}</div>
                </div>
                <button
                  onClick={() => setShowMarksheet(true)}
                  style={{ background: '#007BFF', color: 'white', fontSize: '1rem', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: '700', padding: '10px 20px' }}
                >
                  📋 View Results
                </button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No results for {activeAcademicYear} yet</p>
                <p style={{ fontSize: '14px', color: '#666' }}>Results for the current academic year will appear here once published</p>
              </div>
            )}
          </div>
        </div>
        </>

        )}



        {activeSection === 'idcard' && (
        <>
        <div className="professional-card" style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', border: '2px solid #8b5cf6', boxShadow: '0 8px 25px rgba(139, 92, 246, 0.15)' }}>
          <h3 className="card-title" style={{ color: '#5b21b6', borderBottom: '2px solid #8b5cf6', paddingBottom: '8px' }}>💻 Online Content</h3>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {onlineContent.length > 0 ? (
              <div>
                {['Hindi', 'English', 'Math', 'Science', 'EVS', 'Social_Studies', 'Urdu', 'Sanskrit'].map(subject => {
                  const subjectContent = onlineContent.filter(item => item.subject === subject);
                  if (subjectContent.length === 0) return null;
                  
                  return (
                    <div key={subject} style={{ marginBottom: '15px' }}>
                      <h4 style={{ color: '#007bff', fontSize: '16px', marginBottom: '8px' }}>📚 {subject}</h4>
                      {subjectContent.map(item => (
                        <div key={item._id} style={{ marginBottom: '8px', padding: '8px', background: '#f8f9fa', borderRadius: '4px', borderLeft: '3px solid #007bff' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <strong>{item.title}</strong>
                              <p style={{ margin: '2px 0', fontSize: '14px', color: '#666' }}>{item.description}</p>
                              <small>By: {item.uploadedBy} | {new Date(item.createdAt).toLocaleDateString()}</small>
                            </div>
                            <div>
                              <button 
                                className="btn" 
                                style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)' }}
                                onClick={() => window.location.href = item.resourceLink}
                              >
                                🔗 Open Link
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p>No online content available yet</p>
            )}
          </div>
        </div>

        <div id="idcard" className="professional-card" style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', border: '2px solid #8b5cf6', boxShadow: '0 8px 25px rgba(139, 92, 246, 0.15)' }}>
          <h3 className="card-title" style={{ color: '#5b21b6', borderBottom: '2px solid #8b5cf6', paddingBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            🆔 My ID Card
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                onClick={() => setShowIdCard(true)}
                style={{ 
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
                  color: 'white', 
                  border: 'none', 
                  padding: '6px 12px', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                👁️ View ID
              </button>
              {(user.name === 'admin1' || user.email === 'a1@s.com' || user.role === 'admin1') && (
                <button 
                  onClick={updateSchoolSettings}
                  style={{ 
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
                    color: 'white', 
                    border: 'none', 
                    padding: '6px 12px', 
                    borderRadius: '8px', 
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                >
                  ⚙️ Settings
                </button>
              )}
            </div>
          </h3>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            {(user.name === 'admin1' || user.email === 'a1@s.com' || user.role === 'admin1') && (
              <div style={{ marginBottom: '15px', padding: '10px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', border: '1px solid #f59e0b' }}>
                <p style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', margin: '0' }}>🔧 Admin Controls Available</p>
                <p style={{ fontSize: '12px', color: '#92400e', margin: '5px 0 0 0' }}>You can modify school settings for all ID cards</p>
              </div>
            )}
            {idCard ? (
              <div>
                <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>ID Card Available</p>
                <p style={{ color: '#666', marginBottom: '15px' }}>Click "View ID" to see and download your ID card</p>
                <div style={{ display: 'flex', justifyContent: 'space-around', fontSize: '14px' }}>
                  <div>
                    <strong>Scholar No:</strong><br/>
                    {idCard.scholarNo || 'N/A'}
                  </div>
                  <div>
                    <strong>Mobile:</strong><br/>
                    {idCard.mobile || 'N/A'}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🆔</div>
                <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No ID card available</p>
                <p style={{ fontSize: '14px', color: '#666' }}>Your teacher will create your ID card</p>
              </div>
            )}
          </div>
        </div>
        </>
        )}

        {activeSection === 'games' && (
        <div id="games" className="professional-card" style={{ gridColumn: 'span 2', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: '2px solid #8b5cf6', boxShadow: '0 8px 25px rgba(139, 92, 246, 0.15)' }}>
          <GamesManager 
            userRole={user.role} 
            userClass={user.class} 
            userName={user.name || user.email || user.id}
            userEmail={user.email}
            userId={user.id}
          />
        </div>
        )}

        {activeSection === 'homework' && (
        <div id="homework" className="professional-card" style={{ background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', border: '2px solid #ff9800', boxShadow: '0 8px 25px rgba(255, 152, 0, 0.15)' }}>
          <h3 className="card-title" style={{ color: '#e65100', borderBottom: '2px solid #ff9800', paddingBottom: '8px' }}>📅 Daily Classwork & Homework</h3>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {classworkData.length > 0 ? (
              <div>
                <div style={{ marginBottom: '15px', padding: '10px', background: 'rgba(255, 152, 0, 0.1)', borderRadius: '8px' }}>
                  <strong>Today's Messages:</strong> {classworkData.filter(item => item.date === new Date().toISOString().split('T')[0]).length}
                </div>
                {classworkData.slice(0, 10).map(item => (
                  <div key={item._id} style={{ 
                    marginBottom: '12px', 
                    padding: '12px', 
                    background: item.type === 'classwork' ? '#e3f2fd' : '#fff3e0', 
                    borderRadius: '8px', 
                    borderLeft: `4px solid ${item.type === 'classwork' ? '#2196f3' : '#ff9800'}` 
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <span style={{ fontSize: '16px' }}>{item.type === 'classwork' ? '📚' : '📝'}</span>
                          <strong style={{ color: item.type === 'classwork' ? '#1976d2' : '#f57c00' }}>{item.subject}</strong>
                          <span style={{ fontSize: '12px', color: '#666', background: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: '10px' }}>{item.date}</span>
                        </div>
                        <p style={{ margin: '0', fontSize: '14px', color: '#333', lineHeight: '1.4' }}>{item.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
                <p style={{ fontSize: '16px', marginBottom: '8px', fontWeight: '600' }}>No classwork/homework messages</p>
                <p style={{ fontSize: '14px' }}>Your teacher will send daily messages here</p>
              </div>
            )}
          </div>
        </div>
        )}

        {(user.name === 'admin1' || user.email === 'a1@s.com' || user.role === 'admin1') && (
          <div className="professional-card" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '2px solid #f59e0b', boxShadow: '0 8px 25px rgba(245, 158, 11, 0.15)' }}>
            <h3 className="card-title" style={{ color: '#92400e', borderBottom: '2px solid #f59e0b', paddingBottom: '8px' }}>🔧 Admin Controls</h3>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏢</div>
              <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>School Settings Management</p>
              <p style={{ fontSize: '14px', color: '#92400e', marginBottom: '15px' }}>Configure school information for all ID cards</p>
              <button 
                onClick={updateSchoolSettings}
                style={{ 
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
                  color: 'white', 
                  border: 'none', 
                  padding: '12px 24px', 
                  borderRadius: '10px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
                  width: '100%'
                }}
              >
                ⚙️ Update School Settings
              </button>
              <div style={{ marginTop: '15px', fontSize: '12px', color: '#92400e' }}>
                <p><strong>Current Settings:</strong></p>
                <p>School: {localStorage.getItem('schoolName') || 'The Rising School'}</p>
                <p>Address: {localStorage.getItem('schoolAddress') || 'Savina, Udaipur'}</p>
                <p>Phone: {localStorage.getItem('schoolPhone') || '8209683549'}</p>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'library' && (
        <div id="library" className="professional-card" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', border: '2px solid #10b981', boxShadow: '0 8px 25px rgba(16, 185, 129, 0.15)' }}>
          <h3 className="card-title" style={{ color: '#065f46', borderBottom: '2px solid #10b981', paddingBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            📚 Digital Library
            <button 
              onClick={() => setShowLibraryModal(true)}
              style={{ 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                color: 'white', 
                border: 'none', 
                padding: '6px 12px', 
                borderRadius: '8px', 
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600'
              }}
            >
              📖 Browse
            </button>
          </h3>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {courseLinks.length > 0 ? (
              courseLinks.map(link => (
                <div key={link._id} style={{ marginBottom: '10px', padding: '10px', background: '#f8f9fa', borderRadius: '5px', borderLeft: '3px solid #28a745' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong>{link.title}</strong>
                      <p style={{ margin: '2px 0', fontSize: '14px', color: '#666' }}>{link.description}</p>
                      <small>Type: {link.linkType} | By: {link.uploadedBy} | {parseFromDatabase(link.createdAt, { month: 'short', day: 'numeric' })}</small>
                    </div>
                    <div>
                      <button 
                        className="btn" 
                        style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)' }}
                        onClick={() => window.location.href = link.resourceLink}
                      >
                        🔗 Access Link
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p>No course links available for your class yet</p>
            )}
          </div>
        </div>
        )}

      </div>
      </StudentHome>

      {showLibraryModal && (
        <MobilePopup 
          isOpen={showLibraryModal} 
          onClose={() => setShowLibraryModal(false)}
          title="📚 Digital Library"
        >
          <div style={{ padding: '0 20px 20px 20px' }}>
            <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px', textAlign: 'center' }}>Educational resources for Class: {user.class}</p>
            
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {[...onlineContent, ...courseLinks].length > 0 ? (
                  <div>
                    {['Hindi', 'English', 'Math', 'Science', 'EVS', 'Social_Studies', 'Urdu', 'Sanskrit'].map(subject => {
                      const subjectContent = onlineContent.filter(item => item.subject === subject);
                      const subjectLinks = courseLinks.filter(item => item.subject === subject);
                      const allContent = [...subjectContent, ...subjectLinks];
                      if (allContent.length === 0) return null;
                      
                      return (
                        <div key={subject} style={{ marginBottom: '20px' }}>
                          <div className="library-subject-header">
                            <span style={{ fontSize: '20px' }}>📚</span>
                            <h4 style={{ color: '#0c4a6e', fontSize: '16px', margin: '0', fontWeight: '600' }}>{subject}</h4>
                          </div>
                          {subjectContent.map(item => (
                            <div key={item._id} className="library-content-item">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px' }}>
                                <div style={{ flex: 1 }}>
                                  <h5 style={{ margin: '0 0 6px 0', color: '#0c4a6e', fontSize: '14px', fontWeight: '600' }}>{item.title}</h5>
                                  <p style={{ margin: '0 0 8px 0', color: '#374151', fontSize: '12px', lineHeight: '1.4' }}>{item.description}</p>
                                  <div style={{ fontSize: '10px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>👨🏫 {item.uploadedBy}</span>
                                    <span>•</span>
                                    <span>📅 {new Date(item.createdAt).toLocaleDateString()}</span>
                                  </div>
                                </div>
                                <button 
                                  className="btn"
                                  onClick={() => window.location.href = item.resourceLink}
                                  style={{ 
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                                    fontSize: '11px',
                                    padding: '8px 12px',
                                    minWidth: '70px',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  🔗 Open
                                </button>
                              </div>
                            </div>
                          ))}
                          {courseLinks.filter(item => item.subject === subject).map(item => (
                            <div key={item._id} className="library-content-item" style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', border: '1px solid #8b5cf6' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px' }}>
                                <div style={{ flex: 1 }}>
                                  <h5 style={{ margin: '0 0 6px 0', color: '#5b21b6', fontSize: '14px', fontWeight: '600' }}>{item.title}</h5>
                                  <p style={{ margin: '0 0 8px 0', color: '#374151', fontSize: '12px', lineHeight: '1.4' }}>{item.description}</p>
                                  <div style={{ fontSize: '10px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>📹 {item.linkType}</span>
                                    <span>•</span>
                                    <span>👨🏫 {item.uploadedBy}</span>
                                    <span>•</span>
                                    <span>📅 {new Date(item.createdAt).toLocaleDateString()}</span>
                                  </div>
                                </div>
                                <button 
                                  className="btn"
                                  onClick={() => window.location.href = item.resourceLink}
                                  style={{ 
                                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
                                    fontSize: '11px',
                                    padding: '8px 12px',
                                    minWidth: '70px',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  🔗 Access
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                    {/* Complete Course Links Section */}
                    {courseLinks.filter(item => !item.subject || item.subject === 'Complete Course').length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <div className="library-subject-header" style={{ background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', border: '1px solid #10b981' }}>
                          <span style={{ fontSize: '20px' }}>🎓</span>
                          <h4 style={{ color: '#065f46', fontSize: '16px', margin: '0', fontWeight: '600' }}>Complete Course</h4>
                        </div>
                        {courseLinks.filter(item => !item.subject || item.subject === 'Complete Course').map(item => (
                          <div key={item._id} className="library-content-item" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', border: '1px solid #10b981' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px' }}>
                              <div style={{ flex: 1 }}>
                                <h5 style={{ margin: '0 0 6px 0', color: '#065f46', fontSize: '14px', fontWeight: '600' }}>{item.title}</h5>
                                <p style={{ margin: '0 0 8px 0', color: '#374151', fontSize: '12px', lineHeight: '1.4' }}>{item.description}</p>
                                <div style={{ fontSize: '10px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span>🎓 Complete Course</span>
                                  <span>•</span>
                                  <span>👨🏫 {item.uploadedBy}</span>
                                  <span>•</span>
                                  <span>📅 {new Date(item.createdAt).toLocaleDateString()}</span>
                                </div>
                              </div>
                              <button 
                                className="btn"
                                onClick={() => window.location.href = item.resourceLink}
                                style={{ 
                                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                                  fontSize: '11px',
                                  padding: '8px 12px',
                                  minWidth: '70px',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                🔗 Access
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-icon">📚</div>
                    <div className="empty-state-title">No course content available</div>
                    <div className="empty-state-description">Your teachers will add learning materials here</div>
                  </div>
                )}
            </div>
          </div>
        </MobilePopup>
      )}

      {showMarksheet && marksheet && (
        <ElegantMarksheet 
          marksheet={marksheet} 
          userProfile={userProfile} 
          onClose={() => setShowMarksheet(false)} 
        />
      )}

      {showChangePassword && (
        <ChangePassword 
          user={user}
          onClose={() => setShowChangePassword(false)}
        />
      )}



      {showIdCard && idCard && (
        <MobilePopup 
          isOpen={showIdCard} 
          onClose={() => setShowIdCard(false)}
          title="🆔 Student ID Card"
        >
          <div style={{ padding: '0 20px 20px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            
            <div data-id-card style={{ 
              width: '100%', 
              maxWidth: '350px', 
              height: '220px', 
              background: '#1e40af',
              borderRadius: '10px', 
              padding: '0',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              border: '2px solid #ffffff',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Header section */}
              <div style={{ 
                background: '#ffffff', 
                padding: '12px 15px', 
                borderRadius: '8px 8px 0 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: '#1e40af',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    color: 'white'
                  }}>
                    <img 
                      src="/logo.png" 
                      alt="School Logo" 
                      style={{
                        width: '35px',
                        height: '35px',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '🏫';
                      }}
                    />
                  </div>
                  <div>
                    <h3 style={{ color: '#1e40af', margin: '0', fontSize: '20px', fontWeight: 'bold' }}>{localStorage.getItem('schoolName') || 'The Rising School'}</h3>
                    <p style={{ margin: '0', fontSize: '14px', fontWeight: 'bold', color: '#64748b' }}>{localStorage.getItem('schoolAddress') || 'Savina, Udaipur'}</p>
                    <p style={{ margin: '0', fontSize: '14px', fontWeight: 'bold', color: '#64748b' }}>📞 {localStorage.getItem('schoolPhone') || '8209683549'}</p>
                  </div>
                </div>
                <div style={{
                  background: '#dc2626',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>{activeAcademicYear || '2026-2027'}</div>
              </div>
              
              {/* Main content */}
              <div style={{ padding: '5px 8px', display: 'flex', gap: '26px', height: 'calc(100% - 64px)' }}>
                {/* Photo section */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    width: '70px',
                    height: '85px',
                    background: userProfile?.profilePicture 
                      ? `url(${userProfile.profilePicture}) center/cover no-repeat` 
                      : '#f3f4f6',
                    border: '2px solid #ffffff',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: '#6b7280',
                    overflow: 'hidden'
                  }}>
                    {!userProfile?.profilePicture && '👤'}
                  </div>
                  <div style={{
                    marginTop: '8px',
                    background: '#ffffff',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#1e40af'
                  }}>SR.No : {idCard.scholarNo || user.id?.slice(-6)}</div>
                </div>
                
                {/* Info section */}
                <div style={{ flex: 1, color: 'white' }}>
                  <div style={{ marginBottom: '2px' }}>
                    <h4 style={{ 
                      margin: '0 0 2px 0', 
                      fontSize: '20px', 
                      fontWeight: 'bold', 
                      color: 'white'
                    }}>{userProfile?.name || idCard.name || user.name}</h4>
                    <p style={{ 
                      margin: '0', 
                      fontSize: '14px', 
                      color: 'rgba(255,255,255,0.9)'
                    }}>Class: {user.class}</p>
                  </div>
                  
                  <div style={{ fontSize: '14px', lineHeight: '1.2', color: 'rgba(255,255,255,0.95)', padding: '0 2px 0 0' }}>
                    <div style={{ marginBottom: '1px' }}>
                      <span style={{ fontWeight: 'bold' }}>DOB: </span>
                      <span>{idCard.dob ? new Date(idCard.dob).toLocaleDateString('en-GB') : 'N/A'}</span>
                    </div>
                    <div style={{ marginBottom: '1px' }}>
                      <span style={{ fontWeight: 'bold' }}>Father: </span>
                      <span>{(idCard.fathersName || 'N/A').length > 15 ? (idCard.fathersName || 'N/A').substring(0, 15) + '...' : (idCard.fathersName || 'N/A')}</span>
                    </div>
                    <div style={{ marginBottom: '1px' }}>
                      <span style={{ fontWeight: 'bold' }}>Mobile: </span>
                      <span>{idCard.mobile || 'N/A'}</span>
                    </div>
                    <div>
                      <span style={{ fontWeight: 'bold' }}>Address: </span>
                      <span>{(idCard.address || 'N/A').length > 20 ? (idCard.address || 'N/A').substring(0, 20) + '...' : (idCard.address || 'N/A')}</span>
                    </div>
                  </div>
                  
                  {/* Validity section */}
                  <div style={{
                    position: 'absolute',
                    bottom: '10px',
                    right: '10px',
                    background: 'rgba(255,255,255,0.2)',
                    padding: '4px 8px',
                    borderRadius: '4px'
                  }}>
                    <div style={{ fontSize: '7px', color: 'white', textAlign: 'center' }}>
                      <div>Valid Until</div>
                      <div>March {(activeAcademicYear || '2026-2027').split('-')[1]}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px', flexWrap: 'wrap' }}>
              <button 
                onClick={downloadIdCard}
                style={{ 
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
                  color: 'white', 
                  border: 'none', 
                  padding: '12px 24px', 
                  borderRadius: '10px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                💾 Download ID Card
              </button>
              {(user.name === 'admin1' || user.email === 'a1@s.com' || user.role === 'admin1') && (
                <button 
                  onClick={updateSchoolSettings}
                  style={{ 
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
                    color: 'white', 
                    border: 'none', 
                    padding: '12px 20px', 
                    borderRadius: '10px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  ⚙️ School Settings
                </button>
              )}
            </div>
          </div>
        </MobilePopup>
      )}


    </div>
  );
}

export default StudentDashboard;
